<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  question: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['approve', 'reject', 'close']);

const editedAnswer = ref('');
const rejectionReason = ref('');
const showRejectForm = ref(false);

// Check if answer is valid
const canApprove = computed(() => editedAnswer.value.trim().length > 0);

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen && props.question) {
      editedAnswer.value = props.question.predicted_answer || '';
      rejectionReason.value = '';
      showRejectForm.value = false;
    }
  }
);

const handleApprove = () => {
  if (!canApprove.value) return;
  emit('approve', props.question, editedAnswer.value);
};

const handleReject = () => {
  emit('reject', props.question, rejectionReason.value || null);
};

const handleClose = () => {
  emit('close');
};

const toggleRejectForm = () => {
  showRejectForm.value = !showRejectForm.value;
};
</script>

<template>
  <div
    v-if="isOpen && question"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click.self="handleClose"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between"
      >
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ t('OMNI.PENDING_QUESTIONS.REVIEW_QUESTION') }}
          </h2>
          <p
            v-if="question.agent_bot"
            class="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-1"
          >
            <span class="i-lucide-bot w-4 h-4" />
            {{ question.agent_bot.name }}
          </p>
        </div>
        <button
          class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          @click="handleClose"
        >
          <span class="i-lucide-x w-5 h-5" />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6 space-y-4">
        <!-- Question (read-only) -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.PENDING_QUESTIONS.QUESTION') }}
          </label>
          <div
            class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            {{ question.question }}
          </div>
        </div>

        <!-- Editable Answer -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.PENDING_QUESTIONS.ANSWER') }}
            <span class="text-red-500">*</span>
          </label>
          <textarea
            v-model="editedAnswer"
            rows="6"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            :placeholder="t('OMNI.PENDING_QUESTIONS.ANSWER_PLACEHOLDER')"
          />
          <p class="mt-1 text-xs text-slate-500">
            {{ t('OMNI.PENDING_QUESTIONS.ANSWER_HELP') }}
          </p>
        </div>

        <!-- Rejection form (toggle) -->
        <div v-if="showRejectForm" class="space-y-2">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('OMNI.PENDING_QUESTIONS.REJECTION_REASON') }}
            <span class="text-xs text-slate-500"
              >({{ t('OMNI.COMMON.OPTIONAL') }})</span
            >
          </label>
          <textarea
            v-model="rejectionReason"
            rows="3"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            :placeholder="t('OMNI.PENDING_QUESTIONS.REJECTION_REASON_PLACEHOLDER')"
          />
        </div>

        <!-- Info box -->
        <div
          class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800"
        >
          <p class="text-xs text-blue-700 dark:text-blue-300">
            <span class="i-lucide-info w-3 h-3 inline-block mr-1" />
            {{ t('OMNI.PENDING_QUESTIONS.APPROVE_INFO') }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-between"
      >
        <div>
          <button
            v-if="!showRejectForm"
            type="button"
            class="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md flex items-center gap-1"
            @click="toggleRejectForm"
          >
            <span class="i-lucide-x-circle w-4 h-4" />
            {{ t('OMNI.PENDING_QUESTIONS.REJECT') }}
          </button>
          <button
            v-else
            type="button"
            class="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md flex items-center gap-1"
            @click="handleReject"
          >
            <span class="i-lucide-x-circle w-4 h-4" />
            {{ t('OMNI.PENDING_QUESTIONS.CONFIRM_REJECT') }}
          </button>
        </div>

        <div class="flex gap-2">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md"
            @click="handleClose"
          >
            {{ t('OMNI.COMMON.CANCEL') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-md flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!canApprove"
            @click="handleApprove"
          >
            <span class="i-lucide-check-circle w-4 h-4" />
            {{ t('OMNI.PENDING_QUESTIONS.APPROVE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

